import { useCallback, useMemo, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { STEPS, TOTAL_STEPS, APPLICATION_TYPES } from '../constants';

/**
 * Hook for step validation and URL security
 *
 * @param {Object} selections - Current form selections
 * @param {number} currentStep - Current step from URL
 * @param {Function} setUrlState - Function to update URL state
 * @returns {Object} Validation functions and state
 */
export function useStepValidation(selections, currentStep, setUrlState) {
  const { t } = useTranslation('sprayCalculator');
  const hasShownWarningRef = useRef(false);

  // Validate if a step is complete
  const isStepValid = useCallback(
    (step) => {
      switch (step) {
        case STEPS.SPRAYER:
          return (
            parseFloat(selections.tankVolume) > 0 &&
            selections.tankVolumeUnitId !== null
          );

        case STEPS.AREA: {
          const baseValid =
            parseFloat(selections.totalArea) > 0 &&
            selections.areaUnitId !== null &&
            !!selections.applicationType;

          if (!baseValid) return false;

          // Band application requires additional fields
          if (selections.applicationType === APPLICATION_TYPES.BAND) {
            const hasBandPercentage = parseFloat(selections.bandPercentage) > 0;
            const hasRowAndBand =
              parseFloat(selections.rowSpacing) > 0 &&
              selections.rowSpacingUnitId !== null &&
              parseFloat(selections.bandWidth) > 0 &&
              selections.bandWidthUnitId !== null;

            return hasBandPercentage || hasRowAndBand;
          }

          return true;
        }

        case STEPS.TREATMENT:
          return (
            parseFloat(selections.waterVolume) > 0 &&
            selections.waterVolumeUnitId !== null &&
            selections.products.length >= 1 &&
            selections.products.every(
              (p) => p.id && parseFloat(p.dose) > 0 && p.unitId
            )
          );

        default:
          return false;
      }
    },
    [selections]
  );

  // Can proceed to next step?
  const canProceedToNextStep = useMemo(() => {
    return isStepValid(currentStep);
  }, [currentStep, isStepValid]);

  // Can calculate?
  const canCalculate = useMemo(() => {
    return (
      isStepValid(STEPS.SPRAYER) &&
      isStepValid(STEPS.AREA) &&
      isStepValid(STEPS.TREATMENT)
    );
  }, [isStepValid]);

  // Calculate max accessible step
  const getMaxAccessibleStep = useCallback(() => {
    let maxStep = STEPS.SPRAYER;

    if (isStepValid(STEPS.SPRAYER)) {
      maxStep = STEPS.AREA;
    }
    if (isStepValid(STEPS.SPRAYER) && isStepValid(STEPS.AREA)) {
      maxStep = STEPS.TREATMENT;
    }

    return maxStep;
  }, [isStepValid]);

  // Show warning with debounce
  const showWarning = useCallback(
    (messageKey) => {
      if (!hasShownWarningRef.current) {
        message.warning(t(messageKey));
        hasShownWarningRef.current = true;
        setTimeout(() => {
          hasShownWarningRef.current = false;
        }, 3000);
      }
    },
    [t]
  );

  // Validate step from URL (security check)
  useEffect(() => {
    // Check if step is within valid range
    if (currentStep < STEPS.SPRAYER || currentStep > TOTAL_STEPS) {
      showWarning('validation_step_out_of_range');
      setUrlState({ step: STEPS.SPRAYER });
      return;
    }

    // Check if user is trying to skip steps
    if (currentStep > STEPS.SPRAYER) {
      const maxAccessibleStep = getMaxAccessibleStep();

      if (currentStep > maxAccessibleStep) {
        showWarning('validation_complete_previous_steps');
        setUrlState({ step: maxAccessibleStep });
      }
    }
  }, [currentStep, getMaxAccessibleStep, setUrlState, showWarning]);

  return {
    isStepValid,
    canProceedToNextStep,
    canCalculate,
    getMaxAccessibleStep,
  };
}

export default useStepValidation;
